import React, { useState } from "react";
import { showToast } from "../../../utils/toast";
import { formatPhoneNumber, getPhoneNumberError } from "../../../utils/common";
import {
  ModalField,
  ModalFields,
  ModalFile,
  ModalForm,
  ModalInput,
} from "../../../design-system/patterns/modal-form";

/**
 * Buyer profile edit. One of the nine modal forms.
 *
 * The payload is unchanged and it is deliberately narrow: only `full_name`,
 * `phone_number` and an optional `profile_image` are sent, which is exactly what
 * `updateBuyerProfile` accepts.
 *
 * The commented-out address block that used to sit in the middle of this file
 * is gone rather than converted. A buyer's addresses are owned by the Saved
 * Addresses section, which is a real CRUD surface with a default flag; a second
 * write path for the same data was the reason the block was commented out in
 * the first place.
 *
 * Email stays visible and stays LOCKED, because it is the account identity and
 * changing it is a verification flow, not a text field. `readOnly` plus the
 * track's locked treatment, never a disabled input: a disabled field is skipped
 * by the tab order, so a screen reader user would never learn which account
 * they are editing.
 */

const BuyerEditProfileForm = ({
  open = true,
  initial,
  onSubmit,
  onCancel,
  submitting,
}) => {
  const [formState, setFormState] = useState({
    full_name: initial?.full_name || "",
    email: initial?.email || "",
    phone_number: initial?.phone_number || "",
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Format phone number as user types
    if (name === "phone_number") {
      processedValue = formatPhoneNumber(value, formState.phone_number);

      // Clear phone error when user starts typing
      if (errors.phone_number) {
        setErrors((prev) => ({ ...prev, phone_number: null }));
      }
    }

    setFormState((s) => ({ ...s, [name]: processedValue }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const maxSize = 5 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        showToast.error("File size must be less than 5MB");
        e.target.value = "";
        return;
      }
      setFile(selectedFile);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.full_name?.trim()) {
      newErrors.full_name = "Full name is required";
    }

    if (formState.phone_number) {
      const phoneError = getPhoneNumberError(formState.phone_number);
      if (phoneError) {
        newErrors.phone_number = phoneError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      showToast.error("Please fix the errors before submitting");
      return;
    }

    const fd = new FormData();
    fd.append("full_name", formState.full_name || "");
    fd.append("phone_number", formState.phone_number || "");
    if (file) fd.append("profile_image", file);
    onSubmit?.(fd);
  };

  return (
    <ModalForm
      open={open}
      title="Edit profile"
      onClose={onCancel}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Save changes"
      submittingLabel="Saving"
    >
      <ModalFields cols={2}>
        <ModalField label="Full name" error={errors.full_name}>
          <ModalInput
            name="full_name"
            value={formState.full_name}
            onChange={handleChange}
            autoComplete="name"
            required
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Email" hint="Contact support to change your email address.">
          <ModalInput
            name="email"
            type="email"
            value={formState.email}
            readOnly
            locked
            autoComplete="username"
          />
        </ModalField>

        <ModalField label="Phone" error={errors.phone_number}>
          <ModalInput
            name="phone_number"
            type="tel"
            value={formState.phone_number}
            onChange={handleChange}
            placeholder="071 234 5678"
            autoComplete="tel"
            disabled={submitting}
          />
        </ModalField>

        <ModalField label="Profile image" optional>
          <ModalFile
            accept="image/*"
            onChange={handleFileChange}
            disabled={submitting}
            note="JPG or PNG, up to 5MB."
          />
        </ModalField>
      </ModalFields>
    </ModalForm>
  );
};

export default BuyerEditProfileForm;
